import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useInView } from "react-intersection-observer";
import { createUseStyles } from "react-jss";

import { fetchApi, uploadPosterImageDataUrl } from "../../../services/api";
import TextInput from "../inputs/TextInput";
import { allowsImageUpload, mediaFromUploadedUrl } from "./uploadedMedia";

const ITEMS_PER_PAGE = 15;

export const MediaContext = createContext(() => {});

export const useMediaSelector = (initial = {}) => {
  const [media, setMedia] = useState(initial);
  const context = useContext(MediaContext);

  return [
    media,
    (types = [], tags = []) =>
      context(m => setMedia(m ?? initial), types, tags),
    setMedia,
  ];
};

const useStyles = createUseStyles({
  background: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100%",
    display: "flex",
    "align-items": "center",
    "justify-content": "center",
    opacity: props => (props.show ? 1 : 0),
    transition: "opacity 0.2s ease",
    "pointer-events": props => (props.show ? "auto" : "none"),
    "z-index": 100,
  },
  dim: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    "background-color": "rgba(0, 0, 0, 0.7)",
    "z-index": -1,
  },
  loading: {
    color: "white",
  },
  container: {
    width: "90%",
    "max-width": 700,
    "background-color": "#313131",
    "padding-bottom": 15,
    "border-radius": 5,
    "box-sizing": "border-box",
    "& h1": {
      color: "white",
    },
    "& > *": {
      padding: [0, 15],
    },
  },
  results: {
    width: "100%",
    height: "50vh",
    overflow: "auto",
    "box-sizing": "border-box",
    "margin-top": 15,
    padding: 0,
    position: "relative",

    "& > p": {
      "text-align": "center",
      color: "white",
    },
  },
  result: {
    width: "100%",
    display: "flex",
    "flex-direction": "row",
    padding: [5, 15],
    "box-sizing": "border-box",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
    "&:hover": {
      "background-color": "#373737",
    },
  },
  preview: {
    height: 50,
    width: 50,
    "margin-right": 15,
    border: "1px solid #707070",
    "border-radius": 3,
    "background-size": "cover",
    display: "flex",
    "justify-content": "center",
    "align-items": "center",
    color: "#707070",
    "font-size": 9,
  },
  title: {
    color: "white",
    margin: 0,
    "font-size": 18,
  },
  tags: {
    margin: 0,
    "& span": {
      color: "white",
      "background-color": "#3c7dbc",
      "border-radius": 3,
      padding: [0, 5],
      "margin-right": 2,
      "font-size": 12,
    },
  },
  message: {
    color: "white",
    margin: 0,
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
  matchMode: {
    color: "white",
    "& span": {
      "text-decoration": "underline",
      cursor: "pointer",
    },
  },
  header: {
    display: "flex",
    "align-items": "center",
    "justify-content": "space-between",
    gap: 12,
    "& h1": {
      color: "white",
      margin: 0,
    },
  },
  uploadButton: {
    "background-color": "#3c7dbc",
    color: "white",
    border: "none",
    height: 32,
    width: 70,
    "border-radius": 3,
    cursor: "pointer",
    "flex-shrink": 0,
    "&:disabled": {
      opacity: 0.6,
      cursor: "default",
    },
  },
});

const RecursiveList = ({ body, offset, setResults, account }) => {
  // "waiting" | "loading" | "more" | "done"
  const [status, setStatus] = useState("waiting");
  const current = useRef();
  const [ref, inView] = useInView();

  const offsetBody = useMemo(() => {
    if (body) {
      const nBody = new URLSearchParams(body);
      nBody.set("offset", offset);
      return nBody;
    } else {
      return null;
    }
  }, [body, offset]);

  useEffect(() => {
    if (
      offsetBody !== null &&
      (inView || offsetBody.get("offset") === "0") &&
      offsetBody !== current.current
    ) {
      current.current = offsetBody;
      setStatus("loading");
      fetchApi(`/account/${account}/media/list`, {
        body: offsetBody,
        method: "post",
        version: "v2",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        },
      })
        .then(res => {
          setResults(data =>
            offsetBody.get("offset") === "0"
              ? res.data
              : [...data, ...res.data],
          );
          setStatus(res.more_results ? "more" : "done");
        })
        .catch(err => {
          console.error(err);
          setStatus("error");
        });
    }
  }, [offsetBody, inView, status, account, setResults]);

  switch (status) {
    case "waiting":
      return <div ref={ref} style={{ height: 200 }} />;
    case "more":
      return (
        <RecursiveList
          body={body}
          offset={offset + ITEMS_PER_PAGE}
          setResults={setResults}
          account={account}
        />
      );
    case "loading":
      return <p>Loading...</p>;
    case "error":
      return <p>An error has occured</p>;
    default:
      return null;
  }
};

const MediaSelector = ({
  show,
  onClose,
  onSubmit,
  types,
  tags: initialTags,
  account,
}) => {
  const [results, setResults] = useState([]);
  const [search, setSearch] = useState("");
  const [matchMode, setMatchMode] = useState("all");
  const [query, setQuery] = useState(null);
  const [tags, setTags] = useState("");
  const debounce = useRef();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const canUpload = allowsImageUpload(types);
  const classes = useStyles({ show });

  useEffect(() => {
    setTags(initialTags.join(", "));
  }, [initialTags]);

  useEffect(() => {
    if (show) setUploading(false);
  }, [show]);

  // reset results
  useEffect(() => {
    if (show) {
      setResults([]);
      setQuery(null);
    }
  }, [show]);

  useEffect(() => {
    clearTimeout(debounce.current);
    const body = new URLSearchParams();
    // make sure enabled and public (so we have public url)
    body.append("findBy[enabled]", "1");
    body.append("findBy[tagMatchMode]", matchMode);
    // add file types
    types.forEach(type => body.append("findBy[fileType][]", type));
    // add tags
    tags
      .split(",")
      .filter(Boolean)
      .forEach(tag => body.append("findBy[tags][]", tag.trim()));
    // make sure response format is generic for pagination
    body.append("limit", ITEMS_PER_PAGE);
    body.append("_outputFormat", "generic");

    if (search) body.append("search[value]", search);

    if (show) {
      debounce.current = setTimeout(() => setQuery(body), 100);
    }
  }, [show, types, tags, account, search, matchMode]);

  const toggleMatchMode = () =>
    setMatchMode(m => (m === "any" ? "all" : "any"));

  const onUploadFileChange = event => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      window.alert("Please choose an image file.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      if (typeof dataUrl !== "string" || !dataUrl.startsWith("data:image/")) {
        window.alert("Could not read the image as a data URL.");
        return;
      }

      setUploading(true);
      uploadPosterImageDataUrl(dataUrl)
        .then(url => {
          onSubmit(mediaFromUploadedUrl(url, file.name));
        })
        .catch(err => {
          console.error(err);
          window.alert(err.message || "Image upload failed.");
        })
        .finally(() => {
          setUploading(false);
        });
    };
    reader.onerror = () => {
      window.alert("Could not read the file.");
    };
    reader.readAsDataURL(file);
  };

  switch (results.status) {
    case "error":
      console.error(`Could not fetch media`);
      setTimeout(onSubmit, 1000);
      return (
        <div className={classes.background}>
          <div className={classes.dim} />
          <div className={classes.loading}>Error fetching media</div>
        </div>
      );
    default:
      return (
        <div className={classes.background}>
          <div className={classes.dim} onClick={onClose} />
          <div className={classes.container}>
            <div className={classes.header}>
              <h1>Media Selector</h1>
              {canUpload && (
                <>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    style={{ display: "none" }}
                    disabled={uploading}
                    onChange={onUploadFileChange}
                  />
                  <button
                    type="button"
                    className={classes.uploadButton}
                    disabled={uploading}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {uploading ? "....." : "Upload"}
                  </button>
                </>
              )}
            </div>
            <TextInput
              label="Search"
              value={search}
              onChange={s => setSearch(s)}
            />
            <TextInput
              label="Tags (comma separated)"
              value={tags}
              onChange={s => setTags(s)}
            />
            <p className={classes.matchMode}>
              Tag match mode: <span onClick={toggleMatchMode}>{matchMode}</span>
            </p>
            <div className={classes.results}>
              {results.map(item => (
                <div
                  key={item.id}
                  className={classes.result}
                  onClick={() => onSubmit(item)}
                >
                  {item.preview_url ? (
                    <div
                      style={{
                        backgroundImage: `url("${item.preview_url}")`,
                      }}
                      className={classes.preview}
                    />
                  ) : (
                    <div className={classes.preview}>{item.mime_type}</div>
                  )}
                  <div className={classes.info}>
                    <p className={classes.title}>{item.title}</p>
                    <p className={classes.tags}>
                      {item.tags.map(t => (
                        <span key={t.name}>{t.title}</span>
                      ))}
                    </p>
                  </div>
                </div>
              ))}
              <RecursiveList
                account={account}
                body={query}
                setResults={setResults}
                offset={0}
              />
            </div>
          </div>
        </div>
      );
  }
};

const MediaSelectorProvider = ({ children, account }) => {
  const [showMedia, setShowMedia] = useState(false);
  const [mediaTags, setMediaTags] = useState([]);
  const [mediaTypes, setMediaTypes] = useState([]);
  const [mediaCallback, setMediaCallback] = useState(() => () => {});

  const selectMedia = useCallback((callback, types = [], tags = []) => {
    setShowMedia(true);
    setMediaTypes(types);
    setMediaTags(tags);
    setMediaCallback(() => (...args) => {
      setShowMedia(false);
      callback(...args);
    });
  }, []);

  return (
    <MediaContext.Provider value={selectMedia}>
      <MediaSelector
        show={showMedia}
        tags={mediaTags}
        types={mediaTypes}
        onSubmit={mediaCallback}
        onClose={() => setShowMedia(false)}
        account={account}
      />
      {children}
    </MediaContext.Provider>
  );
};

export default MediaSelectorProvider;
